import { useState, useEffect, useCallback } from "react";
import { apiService } from "@/services/api";

interface BadgeCounts {
  notifications: number;
  supportTickets: number;
  totalPending: number;
}

interface UseBadgeCountsReturn {
  counts: BadgeCounts;
  loading: boolean;
  error: string | null;
  refreshCounts: () => Promise<void>;
}

export function useBadgeCounts(
  refreshInterval: number = 30000, // 30 seconds default
  enabled: boolean = true
): UseBadgeCountsReturn {
  const [counts, setCounts] = useState<BadgeCounts>({
    notifications: 0,
    supportTickets: 0,
    totalPending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!enabled) return;

    try {
      setError(null);
      const badgeCounts = await apiService.getBadgeCounts();
      setCounts(badgeCounts);
    } catch (err) {
      console.error("Error fetching badge counts:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch badge counts");
      // Set fallback counts on error
      setCounts({
        notifications: 0,
        supportTickets: 0,
        totalPending: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const refreshCounts = useCallback(async () => {
    setLoading(true);
    await fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    // Initial fetch
    fetchCounts();

    // Set up polling if enabled and interval is positive
    if (enabled && refreshInterval > 0) {
      const interval = setInterval(fetchCounts, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchCounts, enabled, refreshInterval]);

  return {
    counts,
    loading,
    error,
    refreshCounts,
  };
}
